import { STATUS_BAD_REQUEST } from '@ws/shared/utils';

import { SYNC_STATE_ASK_DELTA } from '../../redux/sync/sync.types';
import { TSyncSessionToken } from '../SyncGroup/session.types';
import { classifySyncFailure, isRetryableFailure } from '../sync/classifySyncFailure';

import { SyncState } from './SyncState';
import { LastUpdateIdKeeper } from './lastUpdateIdKeeper';
import {
  IConstructorParams,
  TCommunicateFunc,
  TDeltaFunc,
  TFetchFunc,
  THealthKind,
  THealthSource,
  TOnApplySuccessFunc,
  TOnChangeFunc,
  TOnHealthFunc,
  TOnSyncStateChange,
  TResumeOutcome,
} from './types';

const DELTA_TIMER = 15000;
const RETRY_APPLY_TIMER = 10000;

export class EventTrain<T> {
  private _watchInterval: NodeJS.Timer | undefined = undefined;
  private _runTimeout: NodeJS.Timer | undefined = undefined;

  private _isBlocked = false;
  private _isOffline = false;
  private _isNotVisible = false;
  private _isRunning = false;
  private _retryCount = 0;

  // Жизненный цикл (session fence & quiesce, И3):
  //  _quiescing — сессия закрывается: НОВЫЕ отправки/циклы не стартуют, in-flight
  //               apply завершается durable (dequeue + persist), но Redux/UI-эффекты
  //               (communicate) подавляются как cross-session.
  //  _finalized — сессия финализирована: charge/run/checkDelta/watch — no-op.
  private _quiescing = false;
  private _finalized = false;
  private _isCheckingDelta = false;
  // Промис полного цикла текущего in-flight apply (fetch → dequeue → persist).
  // quiesce ждёт его, чтобы ack успевшего события сохранился в IDB.
  private _inFlight: Promise<void> | null = null;
  // Последний запущенный persist-колбэк (charge/apply/delta). settleInFlight ждёт и
  // его: iDBActService.saveOne сериализует записи FIFO, поэтому ожидание последнего
  // persist гарантирует, что и предыдущие durable-зафиксированы (C1/C3/C6).
  private _pendingPersist: Promise<void> = Promise.resolve();

  private _syncState: SyncState<T>;
  private _lastUpdateIdInConflict: LastUpdateIdKeeper;
  private _sessionToken?: TSyncSessionToken;

  private _fetcherFunc: TFetchFunc<T> | null = null;
  private _deltaFunc: TDeltaFunc | null = null;
  private _communicate: TCommunicateFunc;

  private _onCharge: TOnChangeFunc<T>;
  private _onApplySuccess: TOnApplySuccessFunc<T>;
  private _onSyncStateChange: TOnSyncStateChange<T>;
  private _onHealth?: TOnHealthFunc;

  // Транспорт-здоровье по источникам: эмитим recoverable-failure только на ПЕРЕХОДЕ
  // healthy→unhealthy и recovered только на unhealthy→healthy (без спама).
  private _applyUnhealthy = false;
  private _deltaUnhealthy = false;

  public constructor(params: IConstructorParams<T>) {
    this._isBlocked = params.isBlocked;
    this._isOffline = params.isOffline;
    this._isNotVisible = params.isNotVisible;

    this._syncState = params.syncState;
    this._fetcherFunc = params.fetcherFunc;
    this._deltaFunc = params.deltaFunc;
    this._communicate = params.communicate;
    this._sessionToken = params.sessionToken;

    this._onCharge = params.onCharge;
    this._onApplySuccess = params.onApplySuccess;
    this._onSyncStateChange = params.onSyncStateChange;
    this._onHealth = params.onHealth;

    this._lastUpdateIdInConflict = new LastUpdateIdKeeper();
  }

  // Эмит health-перехода в супервизор. Подавляется при quiesce/finalize (закрывающаяся
  // сессия не должна красить статус — он cross-session, как и communicate).
  private emitHealth(kind: THealthKind, source: THealthSource): void {
    if (this._quiescing || this._finalized || !this._onHealth) {
      return;
    }
    this._onHealth({ session: this._sessionToken, kind, source });
  }

  // Отметить источник (apply/delta) unhealthy/healthy. Эмитим АГРЕГАТНО: recoverable-failure
  // на первом ушедшем в unhealthy источнике, recovered — только когда ОБА снова здоровы.
  // Иначе recovery одного источника преждевременно поставил бы ACTIVE, пока второй ещё
  // в retry (§2.5, MR-3 review W5).
  private setSourceHealth(source: THealthSource, healthy: boolean): void {
    const wasUnhealthy = this._applyUnhealthy || this._deltaUnhealthy;

    if (source === 'apply') {
      this._applyUnhealthy = !healthy;
    } else {
      this._deltaUnhealthy = !healthy;
    }

    const isUnhealthy = this._applyUnhealthy || this._deltaUnhealthy;

    if (isUnhealthy && !wasUnhealthy) {
      this.emitHealth('recoverable-failure', source);
    } else if (!isUnhealthy && wasUnhealthy) {
      this.emitHealth('recovered', source);
    }
  }

  // Снять блок видимости (environment-watcher владеет visibility; §2.7). БЕЗ старта —
  // старт делает resume() у супервизора. Закрывает sticky-hidden (MR-3 review C1).
  public markVisible(): void {
    this._isNotVisible = false;
  }

  // Идемпотентный примитив запуска (I3 §2.7, control-plane owner = супервизор):
  // атомарно снимает isBlocked/isOffline (isNotVisible НЕ трогает — им владеет
  // environment-watcher), запускает ровно один цикл и возвращает исход. Публичный
  // unblock через setIsBlocked удаляется в пользу этого примитива (см. §2.7).
  public resume(): TResumeOutcome {
    if (this._finalized || this._quiescing) {
      return 'disposed';
    }

    this._isBlocked = false;
    this._isOffline = false;

    if (this._isNotVisible) {
      return 'blocked-hidden';
    }

    if (this._syncState.eventQueue.getLength() === 0) {
      // Нечего отправлять — включаем watch (delta) и считаем сессию активной-в-простое.
      this.watch();
      return 'empty-idle';
    }

    if (!this._isRunning) {
      this.scheduleRun(0);
    }
    return 'started';
  }

  // Единая точка исходящей коммуникации. Подавляется при quiesce/finalize:
  // экшены поезда (ON_DELTA_RECEIVED / ASK_DELTA) применяются в контексте активной
  // сессии, а закрывающаяся сессия их порождать не должна (иначе поздняя delta
  // старого проекта прилетит в активный).
  private emit(action: Parameters<TCommunicateFunc>[0]) {
    if (this._quiescing || this._finalized) {
      return;
    }
    this._communicate(action);
  }

  // Персист состояния в IDB (charge / apply-ack / delta / setConflict). Возвращает
  // ПОДАВЛЕННЫЙ промис (провал логируется, но не пробрасывается) и кладёт его в
  // _pendingPersist, чтобы settleInFlight мог дождаться durable-записи.
  //
  // Почему провал НЕ откатывает in-memory dequeue apply'а: событие уже применено на
  // сервере (ack получен), поэтому ре-отправлять его нельзя (сервер без дедупликации
  // eventId → дубликат/500). Полный state.toStorage() пишет ВСЮ очередь, поэтому
  // следующий успешный persist само-лечит IDB. Остаточный разрыв — если провальный
  // persist оказался последней записью перед закрытием вкладки: тогда при переоткрытии
  // событие уйдёт повторно. Полное устранение требует серверной идемпотентности по
  // eventId (deferral) / reconcile MR-3 / crash-consistency I4.
  private trackPersist(
    cb: (state: SyncState<T>) => void | Promise<void>,
    state: SyncState<T>,
  ): Promise<void> {
    const raw = (async () => {
      await cb(state);
    })().catch((error) => {
      console.error('EventTrain persist failed', error);
    });
    this._pendingPersist = raw;
    return raw;
  }

  public setIsBlocked({
    isBlocked,
    isOffline,
    isNotVisible,
  }: {
    isBlocked?: boolean;
    isOffline?: boolean;
    isNotVisible?: boolean;
  }) {
    const wasBlocked = this.checkIfBlocked();

    if (typeof isBlocked !== 'undefined') {
      this._isBlocked = isBlocked;
    }

    if (typeof isOffline !== 'undefined') {
      this._isOffline = isOffline;
    }

    if (typeof isNotVisible !== 'undefined') {
      this._isNotVisible = isNotVisible;
    }

    const isBlockedNow = this.checkIfBlocked();

    if (wasBlocked && !isBlockedNow) {
      console.log('UNBLOCKED - Resuming sync');
      if (!this._isRunning) {
        // Отменяем возможный висящий retry, чтобы не получить два параллельных run() (F22).
        this.scheduleRun(0);
      }
    }
  }

  // Единая точка планирования run(): всегда снимает предыдущий отложенный запуск,
  // поэтому одновременно живёт максимум один запланированный run() (F22).
  // Во время quiesce/finalize новые циклы не планируются.
  private scheduleRun(delay: number) {
    if (this._quiescing || this._finalized) {
      return;
    }
    clearTimeout(this._runTimeout);
    this._runTimeout = setTimeout(() => {
      this.run();
    }, delay);
  }

  public checkIfBlocked(): boolean {
    return this._isBlocked || this._isOffline || this._isNotVisible;
  }

  get lastUpdateIdInConflict(): string | null {
    return this._lastUpdateIdInConflict.get();
  }

  set lastUpdateIdInConflict(lastUpdateIdInConflict: string | null) {
    this._lastUpdateIdInConflict.set(lastUpdateIdInConflict);
  }

  public setConflictLastIdToLastUpdateId() {
    this._syncState.lastUpdateId = this.lastUpdateIdInConflict;
    this.lastUpdateIdInConflict = null;
    this.trackPersist(this._onSyncStateChange, this._syncState);
  }

  public async checkDelta() {
    if (this._finalized || this._quiescing) {
      return;
    }

    if (this.checkIfBlocked()) {
      return;
    }

    // Re-entry guard: watch() тикает каждые 15 с; если запрос дольше — не запускаем
    // второй параллельный checkDelta.
    if (this._isCheckingDelta) {
      return;
    }
    this._isCheckingDelta = true;

    try {
      console.log('check backend');
      const lastUpdateIdToUse = this.lastUpdateIdInConflict || this._syncState?.lastUpdateId;

      if (this._deltaFunc && lastUpdateIdToUse) {
        const response = await this._deltaFunc(lastUpdateIdToUse);

        // Поздний ответ delta после ухода в quiesce/finalize НЕ должен персистить
        // stale syncState — иначе перетёр бы очередь новой сессии того же проекта (C3).
        if (this._quiescing || this._finalized) {
          return;
        }

        if (response?.isOk) {
          // Delta-путь здоров — сообщаем супервизору recovered (если был unhealthy).
          this.setSourceHealth('delta', true);

          if (response.events && response.events.length > 0) {
            this.stopWatch();

            this.lastUpdateIdInConflict = response.upcomingLastUpdateId || '';
            this.trackPersist(this._onApplySuccess, this._syncState);

            this.emit({
              type: 'ON_DELTA_RECEIVED',
              payload: {
                session: this._sessionToken,
                remote: response.events,
                local: this._syncState.eventQueue.getList(),
                notes: response.notes,
              },
            });
            return;
          }

          // this._syncState.lastUpdateId = response.lastUpdateId || null;
        } else if (isRetryableFailure(classifySyncFailure({ status: response?.status }))) {
          // Транзиентный сбой delta (сеть/5xx/408/429) — supervisor покрасит PAUSE(transport).
          // Терминальные сбои delta здесь не эскалируем (delta ретраится watch-интервалом).
          this.setSourceHealth('delta', false);
        }
      }
    } finally {
      this._isCheckingDelta = false;
    }
  }

  public async watch() {
    if (this._finalized || this._quiescing) {
      return;
    }

    if (this.checkIfBlocked()) {
      return;
    }

    console.log('RUN WATCH');

    if (this._watchInterval) {
      return;
    }

    // Периодический checkDelta — fire-and-forget: глушим возможный reject deltaFunc,
    // иначе он станет unhandled rejection (W2).
    this.checkDelta().catch(() => {});

    this._watchInterval = setInterval(() => {
      this.checkDelta().catch(() => {});
    }, DELTA_TIMER);
  }

  public async stopRun() {
    console.log('STOP RUN');
    clearTimeout(this._runTimeout);
    this._runTimeout = undefined;
    this._isRunning = false;
  }

  public async stopWatch() {
    console.log('STOP WATCH');
    clearInterval(this._watchInterval);
    this._watchInterval = undefined;
  }

  public async totalStop() {
    await this.stopRun();
    await this.stopWatch();
  }

  // Перевод в режим тишины: новые отправки/циклы не стартуют, communicate подавлён.
  // Уже летящий apply завершится и durable-персистнет свой ack (см. run()).
  public quiesceMode() {
    this._quiescing = true;
    clearTimeout(this._runTimeout);
    this._runTimeout = undefined;
    clearInterval(this._watchInterval);
    this._watchInterval = undefined;
  }

  // Дождаться завершения (settle) текущего in-flight apply И ВСЕХ persist'ов
  // (charge/apply/delta), чтобы к моменту гидратации новой сессии того же проекта все
  // записи были durable-зафиксированы (C1/C3/C6). Дренируем в цикле: persist,
  // зарегистрированный уже во время ожидания (напр. поздний quiesce-charge), тоже
  // должен войти в барьер — иначе он переживёт finalize и гонится с гидратацией.
  public async settleInFlight(): Promise<void> {
    let prev: Promise<void> | null = null;
    while (this._pendingPersist !== prev) {
      prev = this._pendingPersist;
      // eslint-disable-next-line no-await-in-loop
      await Promise.allSettled([this._inFlight ?? Promise.resolve(), this._pendingPersist]);
    }
  }

  // Терминальная финализация: таймеры сняты, charge/run/checkDelta/watch — no-op.
  public finalize() {
    this._finalized = true;
    this._quiescing = true;
    clearTimeout(this._runTimeout);
    this._runTimeout = undefined;
    clearInterval(this._watchInterval);
    this._watchInterval = undefined;
    this._isRunning = false;
  }

  public async run() {
    console.log('RUN');
    if (this._finalized) {
      return;
    }

    // Re-entry guard: не запускаем второй параллельный run() (F22) — иначе одно
    // событие может уйти дважды (напр. висящий retry-таймер + charge/unblock).
    if (this._isRunning) {
      return;
    }

    if (this.checkIfBlocked()) {
      return;
    }

    // Во время quiesce новые отправки не стартуют (уже летящий apply завершается
    // отдельно и durable-персистится).
    if (this._quiescing) {
      return;
    }

    this._isRunning = true;

    const eventToSend = this._syncState.eventQueue.showFirst();

    if (!eventToSend) {
      this._isRunning = false;
      this.watch();
      return;
    }

    // Полный цикл apply (fetch → обработка ответа → dequeue → persist) оборачиваем
    // в _inFlight, чтобы quiesce мог дождаться durable-сохранения ack.
    const applyChain = this.applyOnce(eventToSend);
    this._inFlight = applyChain;
    try {
      await applyChain;
    } catch (error) {
      // Неожиданный throw (напр. reject fetcherFunc) не должен оставить train
      // навсегда в _isRunning=true — иначе очередь замрёт (W2).
      console.error('Error during apply run', error);
      this._isRunning = false;
    } finally {
      this._inFlight = null;
    }
  }

  private async applyOnce(eventToSend: T) {
    const response = await this.fetch(eventToSend);

    const status = response?.status;

    if (response?.isOk) {
      // Событие принято сервером → снимаем с очереди и продвигаем lastUpdateId.
      this._syncState.eventQueue.takeFirst();
      this._syncState.lastUpdateId = response.data?.upcomingLastUpdateId || null;
      // Await durability: ack должен durable-сохраниться до отпускания settleInFlight.
      // При провале НЕ откатываем и НЕ ре-отправляем (событие уже применено на сервере
      // без дедупликации) — полный state.toStorage() само-лечит IDB следующим успешным
      // persist'ом (см. trackPersist).
      await this.trackPersist(this._onApplySuccess, this._syncState);
      this._retryCount = 0; // Reset on success
      // Транспорт восстановился после серии retry → сообщаем супервизору recovered.
      this.setSourceHealth('apply', true);
    } else if (status === STATUS_BAD_REQUEST) {
      // Только stale lastUpdateId (400) → terminal для apply, запрашиваем delta. Это
      // app-семантика apply-пути, поэтому обрабатывается ДО общего классификатора.
      // 400 + пустая delta = poison (F3) — вне скоупа И2, чинится в И4.
      this._isRunning = false;
      // payload с токеном добавляем только если сессия его задала (реальный путь).
      // Прямо-сконструированные train в юнит-тестах токена не имеют — форма экшена
      // остаётся { type } без payload (регресс-гард И2 не ломается).
      this.emit(
        this._sessionToken
          ? { type: SYNC_STATE_ASK_DELTA, payload: { session: this._sessionToken } }
          : { type: SYNC_STATE_ASK_DELTA },
      );
      this.watch();
      return;
    } else if (!isRetryableFailure(classifySyncFailure({ status }))) {
      // Не-транзиент — ретраем не лечится → halt без retry и без ASK_DELTA.
      this._isRunning = false;
      // 401 (auth-required) НЕ красим sync-error: им ЭКСКЛЮЗИВНО владеет auth-путь
      // (handleUnAuth в Fetcher → auth-change → reconcile → PAUSE(auth-required)), иначе
      // получим двойной/конфликтующий статус (MR-3 review). Прочие terminal (403/404/409/
      // 422/invalid-response/unknown) → terminal-failure → PAUSE(sync-error): иначе UI
      // остался бы ACTIVE при сломанном синке (класс F29). Восстановление PAUSE(sync-error):
      // следующая правка (charge→scheduleRun переотправит очередь) либо reconcile-триггер
      // (visibility/network/re-open → resume→run); для истинно-терминальной ошибки пауза
      // держится до действия пользователя — это корректно.
      if (classifySyncFailure({ status }) !== 'auth-required') {
        this.emitHealth('terminal-failure', 'apply');
      }
      this.watch();
      return;
    } else {
      // Транзиент (5xx / сеть / 408 / 429) → retry с backoff.
      this._isRunning = false;
      this._retryCount += 1;
      // Уход в retry → супервизору recoverable-failure (он покрасит PAUSE(transport)).
      // Backoff-владение остаётся здесь; probe-цикл супервизора НЕ дублирует retry.
      this.setSourceHealth('apply', false);

      const backoffDelay = Math.min(RETRY_APPLY_TIMER * Math.pow(2, this._retryCount - 1), 60000);
      console.log(`Retrying in ${backoffDelay}ms (attempt ${this._retryCount})`);

      this.scheduleRun(backoffDelay);
      return;
    }

    if (this._syncState.eventQueue.getLength() === 0) {
      this._isRunning = false;
      this.watch();
      return;
    }

    // Успешный apply уже обработан (событие снято с очереди). Если за время apply
    // train успел заблокироваться (F23) или уйти в quiesce — НЕ продолжаем цепочку,
    // но сбрасываем _isRunning, чтобы unblock (`setIsBlocked`) корректно возобновил
    // дренаж. Важно: ответ здесь уже применён, поэтому повторной отправки не будет.
    this._isRunning = false;

    if (this.checkIfBlocked()) {
      return;
    }

    this.scheduleRun(0);
  }

  public async fetch(event: T) {
    if (this._fetcherFunc && this._syncState.lastUpdateId) {
      return this._fetcherFunc(event, this._syncState.lastUpdateId);
    }

    return;
  }

  public charge(event: T) {
    console.log('CHARGE');
    // После финализации сессии заряжать нечего (train мёртв).
    if (this._finalized) {
      return;
    }

    this._syncState.eventQueue.pushEvent(event);

    this.trackPersist(this._onCharge, this._syncState);

    // Во время quiesce правку кладём в очередь + персистим (не теряем), но новую
    // отправку не планируем — её выполнит следующая сессия проекта.
    if (this._quiescing) {
      return;
    }

    if (!this._isRunning) {
      this.stopWatch();
      // scheduleRun (не прямой run) — снимает висящий retry-таймер, чтобы он не
      // выстрелил вторым параллельным run() позже (F22).
      this.scheduleRun(0);
    }
  }
}
